import { Properties } from 'csstype';

import { formatNumberWithSpaces } from './functions';
import { formatCellData } from './reports';

export type FillingCondition = {
    from: number | string;
    to: number | string;
    value: string;
};

/**
 * Заливка ячеек обычной (не кастомной) метрики — настройки столбца из «…» → «Заливка ячеек»,
 * хранятся в отчёте (table.cellFills: dataKey → настройки). Поля — как у кастомной метрики
 */
export interface CellFillSettings {
    /** true — «заливать ячейку от значения» (полоса), false — «заливать всю ячейку» */
    isFilling: boolean;
    fillingConditions: FillingCondition[];
}

function toNumberSafe(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string') return null;

    // Убираем пробелы, валюты/проценты и приводим запятую к точке
    const normalized = value
        .replace(/\s+/g, '')
        .replace(/%/g, '')
        .replace(/₽/g, '')
        .replace(/&nbsp;/g, '')
        .replace(',', '.');

    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}

function pickFillColor(conditions: FillingCondition[] | undefined, numericValue: number): string | null {
    if (!Array.isArray(conditions) || conditions.length === 0) return null;

    let color: string | null = null;

    for (const c of conditions) {
        const from = toNumberSafe(c.from);
        const to = toNumberSafe(c.to);
        if (from === null || to === null) continue;
        if (numericValue >= from && numericValue <= to) {
            color = c.value;
        }
    }

    return color;
}

/**
 * Как pickFillColor, но пустое «от» / «до» — без границы: условие без диапазона подходит всем значениям
 * («Заливать всю ячейку» + цвет — закрашен весь столбец). Только для заливки обычных метрик: у кастомных
 * пустая строка условия (по умолчанию #000000) так и остаётся «не условием» — иначе их столбцы стали бы чёрными
 */
function pickFillColorOpenRange(conditions: FillingCondition[], numericValue: number): string | null {
    let color: string | null = null;
    for (const c of conditions) {
        if (!c?.value) continue;
        const from = toNumberSafe(c.from);
        const to = toNumberSafe(c.to);
        if ((from === null || numericValue >= from) && (to === null || numericValue <= to)) {
            color = c.value;
        }
    }
    return color;
}

/**
 * Стиль заливки ячейки обычной метрики по условиям «от – до» — те же правила, что у кастомной
 * (getCustomMetricRow): «вся ячейка» — сплошной фон, «от значения» — полоса длиной |значение| / максимум
 * столбца, строки «Итого» при этом не закрашиваются. Отличие: пустая граница — без границы
 * (pickFillColorOpenRange). null — не число или ни одно условие не подошло
 */
export const getCellFillStyle = (
    settings: CellFillSettings | undefined,
    dataKey: string,
    renderItem: any,
    data: any[],
): Properties | null => {
    if (!settings || !Array.isArray(settings.fillingConditions) || settings.fillingConditions.length === 0) return null;
    const numericValue = toNumberSafe(renderItem?.cellData);
    if (numericValue === null) return null;
    const fillColor = pickFillColorOpenRange(settings.fillingConditions, numericValue);
    if (!fillColor) return null;
    if (!settings.isFilling) return { backgroundColor: fillColor };

    const isTotalRow =
        String(renderItem?.rowData?.pathStr || '').startsWith('total') ||
        String(renderItem?.rowData?.group || '').includes('Итого и среднее');
    if (isTotalRow) return null;

    const result: { [key: string]: { max: number; maxPositive: number; maxNegative: number } } = {};
    processItems(data, result, [ dataKey ]);
    const denom = result[dataKey]
        ? Math.max(result[dataKey].maxPositive, Math.abs(result[dataKey].maxNegative))
        : 0;
    const percent = denom > 0 ? (Math.abs(numericValue) / denom) * 100 : 0;
    const widthPercent = Math.abs(numericValue) !== 0 && percent < 5 ? 5 : Math.min(100, Math.max(0, percent));
    return { backgroundImage: `linear-gradient(to left, ${fillColor} ${widthPercent}%, transparent ${widthPercent}%)` };
};

export const getCustomMetricRow = (dataKey:any,renderItem:any,item:any,currency:string,data:any[], externalStyle?: Properties) => {
    const customStyle: Properties = externalStyle ? { ...externalStyle } : {
        width: '100%',
        backgroundImage: 'unset',
        textAlign: 'right',
        display:'flex',
        justifyContent:'flex-end',
        alignItems:'center',
        height:'38px',
        padding: '10px',
        paddingRight:'4px'

    };
    const formatted = formatCellData(renderItem.cellData, dataKey);
    const resultValue = typeof formatted === 'object'
        ? formatted
        : (() => {
            let text = String(formatted);
            if (item?.unit === 'Percentage' && !text.includes('%')) {
                text = `${text}%`;
            }
            if (item?.unit === 'Currency' && currency && !text.includes(currency)) {
                text = `${currency} ${text}`;
            }
            return text;
        })();
    const numericCellValue = toNumberSafe(renderItem?.cellData);
    if (item?.isFilling === true) {
        // Восстанавливаем старую модель вычисления max/min, но без NaN:
        // считаем только по нужному dataKey, рекурсивно по children.
        const result: { [key: string]: { max: number; maxPositive: number; maxNegative: number } } = {};
        processItems(data, result, [dataKey]);

        let percent = 0;
        if (numericCellValue !== null) {
            const denom = result[dataKey]
                ? Math.max(result[dataKey].maxPositive, Math.abs(result[dataKey].maxNegative))
                : 0;
            percent = denom > 0 ? (Math.abs(numericCellValue) / denom) * 100 : 0;

            // Цвет градиента берётся только из условий заливки.
            // Фон столбца (background) применяется только к заголовку, не к ячейкам данных.
            const fillColor = pickFillColor(item?.fillingConditions as FillingCondition[] | undefined, numericCellValue);

            const isTotalRow =
                String(renderItem?.rowData?.pathStr || '').startsWith('total') ||
                String(renderItem?.rowData?.group || '').includes('Итого и среднее');

            const widthPercent =
                Math.abs(numericCellValue) !== 0 && percent < 5 ? 5 : Math.min(100, Math.max(0, percent));

            // Градиент применяется только если есть подходящее условие заливки
            if (!isTotalRow && fillColor) {
                customStyle.backgroundImage = `linear-gradient(to left, ${fillColor} ${widthPercent}%, transparent ${widthPercent}%)`;
            }
        }

        return (
            <span style={customStyle}>
                {typeof resultValue === 'number' ? formatNumberWithSpaces(resultValue) : resultValue}
    </span>
        );
    }

    try{
        if (numericCellValue !== null) {
            const fillColor =
                pickFillColor(item?.fillingConditions as FillingCondition[] | undefined, numericCellValue) ||
                null;

            // Режим "заливать всю ячейку": сплошная заливка только по условиям заливки.
            // Фон столбца (background) применяется только к заголовку, не к ячейкам данных.
            if (fillColor) {
                customStyle.backgroundColor = fillColor;
            }

            return (
                <span style={customStyle}>
                    {resultValue}
                </span>
            );
        }

        // Фолбэк: если значение не число — просто рендерим как есть.
        // Фон столбца (background) применяется только к заголовку.
        return (
            <span style={customStyle}>
                {resultValue}
            </span>
        );
    }catch(err){
        console.error(err);
    }
};

export const  processItems = (items: any[],result:any,formatGroup:string[]) => {
    items.forEach((item: { [key: string]: any; children?: any[] }) => {
        // Игнорируем элементы с pathStr === "total"
        if (item.pathStr === 'total') return;

        // Обработка текущего элемента
        Object.entries(item).forEach(([ key, value ]) => {
            if (formatGroup.includes(key)) {
                const numericValue = toNumberSafe(value);
                if (numericValue === null) return;
                if (result[key]) {
                    result[key].max = Math.max(result[key].max, numericValue);
                    result[key].maxPositive = Math.max(result[key].maxPositive, numericValue);
                    result[key].maxNegative = Math.min(result[key].maxNegative, numericValue);
                } else {
                    result[key] = {
                        maxNegative: numericValue >= 0 ? 0 : numericValue,
                        maxPositive: numericValue >= 0 ? numericValue : 0,
                        max: numericValue,
                    };
                }
            }
        });

        if (item.children && item.children.length > 0) {
            processItems(item.children,result,formatGroup);
        }
    });
};

